// Lag-compensation history (§18.5, §20.9): a ring of each bean's (x, y, z) for the last
// `historyTicks` ticks. Server only; used for shove/grab/punch hit tests, never for knockback.
import { MAX_BEANS, TUNE } from '../constants';
import type { LagHistory, Vec3, World } from '../types';

const RING = TUNE.lagcomp.historyTicks;
const NO_TICK = Number.NEGATIVE_INFINITY;

export function createLagHistory(): LagHistory {
  return {
    ticks: new Array<number>(RING).fill(NO_TICK),
    x: new Float32Array(RING * MAX_BEANS),
    y: new Float32Array(RING * MAX_BEANS),
    z: new Float32Array(RING * MAX_BEANS),
    head: RING - 1,
  };
}

/** Records every bean's position for `w.tick` (last phase of each step). No-op without history. */
export function record(w: World): void {
  const h = w.hist;
  if (!h) return;
  h.head = (h.head + 1) % RING;
  h.ticks[h.head] = w.tick;
  const base = h.head * MAX_BEANS;
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const b = w.beans[slot];
    h.x[base + slot] = b.pos.x;
    h.y[base + slot] = b.pos.y;
    h.z[base + slot] = b.z;
  }
}

/**
 * Position of `slot` as of tick `w.tick − ticksAgo`, clamped to the oldest recorded entry. Ticks
 * not yet recorded (including the tick being stepped) and worlds without history return the
 * present position.
 */
export function positionAt(w: World, slot: number, ticksAgo: number, out: Vec3): Vec3 {
  const b = w.beans[slot];
  out.x = b.pos.x;
  out.y = b.pos.y;
  out.z = b.z;
  const h = w.hist;
  if (!h || ticksAgo <= 0) return out;
  const newest = h.ticks[h.head];
  const target = w.tick - ticksAgo;
  if (newest === NO_TICK || target > newest) return out;

  let back = Math.min(newest - target, RING - 1);
  let idx = (h.head - back + RING) % RING;
  while (back > 0 && h.ticks[idx] === NO_TICK) {
    back--;
    idx = (h.head - back + RING) % RING;
  }
  const i = idx * MAX_BEANS + slot;
  out.x = h.x[i];
  out.y = h.y[i];
  out.z = h.z[i];
  return out;
}
